import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import history from '../../store/utils/history';
import { selectIsUserAuthenticated } from '../../store/selectors/userSelectors';
import { BUY_PAGE, PORTFOLIO_PAGE } from '../../constants/pages';
import { useIsClosedAccount } from './useIsClosedAccount';
import useCheckPermissions from './useCheckPermissions';

// How long a tab must sit hidden before reactivating it counts as
// "re-entering the app" (PBI 71048) rather than a brief alt-tab.
const REENTRY_IDLE_THRESHOLD_MS = 15 * 60 * 1000;

// PBI 71048 only redirected fresh navigation to "/" (see AppRoutes.js).
// Nothing handled a tab left open on /portfolio being reactivated later —
// this hook covers that case, mirroring PrivateRoute.js's permission/closed-
// account gates so it doesn't fight the redirects already applied there.
export const useReentryRedirect = () => {
  const isUserAuthenticated = useSelector(selectIsUserAuthenticated);
  const isClosedAccount = useIsClosedAccount();
  const { displayBasicAccountOptionsPermission } = useCheckPermissions();
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (!hiddenAt || Date.now() - hiddenAt < REENTRY_IDLE_THRESHOLD_MS) {
        return;
      }

      if (
        !isUserAuthenticated ||
        isClosedAccount ||
        displayBasicAccountOptionsPermission
      ) {
        return;
      }

      if (history.location.pathname === PORTFOLIO_PAGE) {
        history.push(BUY_PAGE);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isUserAuthenticated,
    isClosedAccount,
    displayBasicAccountOptionsPermission,
  ]);
};
